const ENCRYPTION_KEY = process.env.REACT_APP_BRIDGE_ENCRYPTION_KEY ?? '';
const DISABLE_ENCRYPTION = false;
const NONCE_LENGTH_BYTES = 12;
const AUTH_TAG_LENGTH_BYTES = 16;

const importAesGcmKey = (key: string, usage: KeyUsage) => {
    const keyBytes = Uint8Array.from(atob(key), (character) => character.charCodeAt(0));

    return window.crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        [usage],
    );
};

const bytesToBase64 = (bytes: Uint8Array): string => {
    const binaryString = Array.from(bytes)
        .map((byte) => String.fromCharCode(byte))
        .join('');

    return btoa(binaryString);
};

const base64ToBytes = (value: string): Uint8Array => {
    const binaryString = atob(value);

    return Uint8Array.from(binaryString, (character) => character.charCodeAt(0));
};

export const encryptObject = async <T>(
    object: T,
    key: string = ENCRYPTION_KEY,
): Promise<string | null> => {
    try {
        if (DISABLE_ENCRYPTION) {
            return JSON.stringify(object);
        }

        if (!key) {
            throw new Error('Encryption key is not set');
        }

        const cryptoKey = await importAesGcmKey(key, 'encrypt');
        const jsonString = JSON.stringify(object);
        const data = new TextEncoder().encode(jsonString);
        const nonce = window.crypto.getRandomValues(new Uint8Array(NONCE_LENGTH_BYTES));
        const ciphertext = await window.crypto.subtle.encrypt(
            { name: 'AES-GCM', iv: nonce },
            cryptoKey,
            data,
        );

        const combined = new Uint8Array(nonce.length + ciphertext.byteLength);
        combined.set(nonce, 0);
        combined.set(new Uint8Array(ciphertext), nonce.length);

        return bytesToBase64(combined);
    } catch {
        return null;
    }
};

export const decryptString = async <T>(
    encryptedString: string,
    key: string = ENCRYPTION_KEY,
): Promise<T | null> => {
    try {
        if (DISABLE_ENCRYPTION) {
            return JSON.parse(encryptedString) as T;
        }

        if (!key) {
            throw new Error('Encryption key is not set');
        }

        const data = base64ToBytes(encryptedString);
        if (data.length <= NONCE_LENGTH_BYTES + AUTH_TAG_LENGTH_BYTES) {
            throw new Error('Encrypted data too short');
        }

        const nonce = data.slice(0, NONCE_LENGTH_BYTES);
        const ciphertext = data.slice(NONCE_LENGTH_BYTES);
        const cryptoKey = await importAesGcmKey(key, 'decrypt');
        const plaintext = await window.crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: nonce },
            cryptoKey,
            ciphertext,
        );

        const jsonString = new TextDecoder().decode(plaintext);
        return JSON.parse(jsonString) as T;
    } catch {
        return null;
    }
};
